import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Grid, Card, CardActionArea, CardContent, Chip,
  List, ListItem, ListItemText, Paper, CircularProgress, Divider,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import { projectsAPI, tasksAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getTaskType, taskTypeIcon } from '../utils/taskTypes';
import CategoryChip from '../components/pm/CategoryChip';

/**
 * PM home / project overview.
 * - Internal users: just the project cards (full width).
 * - Clients: their reported tasks ("Meine Meldungen") + project cards.
 */
function PMHomePage() {
  const navigate = useNavigate();
  const { user, isClient } = useAuth();
  const clientUser = isClient && isClient();
  const [projects, setProjects] = useState([]);
  const [reported, setReported] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const calls = [projectsAPI.getAll()];
    // Clients are never the assignee (the PM is) -> show what they reported
    if (clientUser) calls.push(tasksAPI.getAll({ mine: true }));
    Promise.allSettled(calls).then((res) => {
      if (res[0].status === 'fulfilled') setProjects(res[0].value.data || []);
      if (clientUser && res[1] && res[1].status === 'fulfilled') setReported(res[1].value.data || []);
      setLoading(false);
    });
  }, [clientUser]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  const greetingName = user?.full_name || user?.username || '';

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Willkommen{greetingName ? `, ${greetingName}` : ''}
      </Typography>
      {user?.customer_name && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {user.customer_name}
        </Typography>
      )}

      <Grid container spacing={3}>
        {/* Clients only: their reported tasks with current status */}
        {clientUser && (
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>Meine Meldungen</Typography>
              <Divider sx={{ mb: 1 }} />
              {reported.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Sie haben noch keine Aufgaben gemeldet.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {reported.map((task) => (
                    <ListItem key={task.id} button alignItems="flex-start"
                      onClick={() => task.project_id && navigate(`/pm/projects/${task.project_id}`)}>
                      <Box component="span" title={getTaskType(task.task_type).label}
                        sx={{ display: 'inline-flex', mt: 0.5, mr: 1, color: getTaskType(task.task_type).color }}>
                        {taskTypeIcon(task.task_type, { sx: { fontSize: 18 } })}
                      </Box>
                      <ListItemText primary={task.name} secondary={task.project_name || null} />
                      {task.category_info && (
                        <CategoryChip category={task.category_info} sx={{ ml: 1, mt: 0.25, height: 20, fontSize: '0.7rem' }} />
                      )}
                      {task.status && (
                        <Chip size="small" label={task.status.name}
                          sx={{ bgcolor: task.status.color || '#999', color: '#fff', ml: 1, mt: 0.25 }} />
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        )}

        {/* Projects */}
        <Grid item xs={12} md={clientUser ? 7 : 12}>
          <Typography variant="h6" fontWeight={600} gutterBottom>Projekte</Typography>
          {projects.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Noch keine Projekte vorhanden
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {projects.map((p) => (
                <Grid item xs={12} sm={6} md={clientUser ? 6 : 4} key={p.id}>
                  <Card variant="outlined" sx={{ borderRadius: 2, borderTop: `4px solid ${p.color || '#1e3a5f'}` }}>
                    <CardActionArea onClick={() => navigate(`/pm/projects/${p.id}`)}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <FolderIcon fontSize="small" sx={{ color: p.color || '#1e3a5f' }} />
                          <Typography variant="subtitle1" fontWeight={600} noWrap>{p.name}</Typography>
                        </Box>
                        {p.customer_name && <Chip size="small" label={p.customer_name} sx={{ mb: 1 }} />}
                        {p.description && (
                          <Typography variant="body2" color="text.secondary" sx={{
                            overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          }}>
                            {p.description}
                          </Typography>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}

export default PMHomePage;
